/**
 * Financial Reports - Trial Balance, Income Statement, Balance Sheet
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  FileText, 
  Download, 
  Loader2, 
  RefreshCw, 
  CheckCircle2, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Scale,
  DollarSign,
  Calculator
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

// Type badge colors
const TYPE_COLORS = {
  ASSET: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  LIABILITY: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  EQUITY: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  INCOME: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  COGS: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  EXPENSE: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
};

// Reusable Report Section Component (moved outside)
const ReportSection = ({ title, items, total, icon: Icon, color }) => (
  <Card className="overflow-hidden">
    <CardHeader className={`py-3 ${color}`}>
      <CardTitle className="text-sm font-medium flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4" />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      <Table>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={2} className="text-center text-muted-foreground py-4">
                No activity
              </TableCell>
            </TableRow>
          ) : (
            items.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <span className="font-mono text-sm text-muted-foreground mr-2">{item.account_code}</span>
                  {item.account_name}
                </TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(item.amount)}</TableCell>
              </TableRow>
            ))
          )}
          <TableRow className="bg-slate-50 dark:bg-slate-800 font-semibold">
            <TableCell>Total {title}</TableCell>
            <TableCell className="text-right font-mono">{formatCurrency(total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

// Balance Sheet Section Component (moved outside)
const BalanceSheetSection = ({ title, items, total, color }) => (
  <Card className="overflow-hidden">
    <CardHeader className={`py-3 ${color}`}>
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      <Table>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={2} className="text-center text-muted-foreground py-4">
                No items
              </TableCell>
            </TableRow>
          ) : (
            items.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <span className="font-mono text-sm text-muted-foreground mr-2">{item.account_code}</span>
                  {item.account_name}
                </TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(item.amount)}</TableCell>
              </TableRow>
            ))
          )}
          <TableRow className="bg-slate-50 dark:bg-slate-800 font-semibold">
            <TableCell>Total {title}</TableCell>
            <TableCell className="text-right font-mono">{formatCurrency(total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

// Trial Balance Report Component
const TrialBalanceReport = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">As of {data.as_of_date}</h3>
          <p className="text-sm text-muted-foreground">
            Generated: {new Date(data.generated_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.is_balanced ? (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Balanced
            </Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Unbalanced
            </Badge>
          )}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Code</TableHead>
                <TableHead>Account Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-mono text-sm">{row.account_code}</TableCell>
                  <TableCell>{row.account_name}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[row.account_type] || ''}`}>
                      {row.account_type}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {row.debit > 0 ? formatCurrency(row.debit) : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {row.credit > 0 ? formatCurrency(row.credit) : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {/* Totals Row */}
              <TableRow className="bg-slate-50 dark:bg-slate-800 font-bold">
                <TableCell colSpan={3} className="text-right">TOTALS</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(data.total_debits)}</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(data.total_credits)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Income Statement Report Component
const IncomeStatementReport = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="font-medium">Period: {data.period_start} to {data.period_end}</h3>
        <p className="text-sm text-muted-foreground">
          Generated: {new Date(data.generated_at).toLocaleString()}
        </p>
      </div>

      {/* Income Section */}
      <ReportSection 
        title="Revenue" 
        items={data.income.items} 
        total={data.income.total}
        icon={TrendingUp}
        color="bg-green-50 dark:bg-green-900/20"
      />

      {/* COGS Section */}
      <ReportSection 
        title="Cost of Goods Sold" 
        items={data.cogs.items} 
        total={data.cogs.total}
        icon={DollarSign}
        color="bg-amber-50 dark:bg-amber-900/20"
      />

      {/* Gross Profit */}
      <Card className="border-2 border-blue-200 dark:border-blue-800">
        <CardContent className="py-4 flex items-center justify-between">
          <span className="font-semibold">Gross Profit</span>
          <span className={`text-xl font-bold font-mono ${data.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(data.gross_profit)}
          </span>
        </CardContent>
      </Card>

      {/* Expenses Section */}
      <ReportSection 
        title="Operating Expenses" 
        items={data.expenses.items} 
        total={data.expenses.total}
        icon={TrendingDown}
        color="bg-orange-50 dark:bg-orange-900/20"
      />

      {/* Net Income */}
      <Card className={`border-2 ${data.net_income >= 0 ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-red-500 bg-red-50 dark:bg-red-900/20'}`}>
        <CardContent className="py-6 flex items-center justify-between">
          <span className="text-lg font-bold">Net Income</span>
          <span className={`text-2xl font-bold font-mono ${data.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(data.net_income)}
          </span>
        </CardContent>
      </Card>
    </div>
  );
};

// Balance Sheet Report Component
const BalanceSheetReport = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">As of {data.as_of_date}</h3>
          <p className="text-sm text-muted-foreground">
            Generated: {new Date(data.generated_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.is_balanced ? (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Balanced
            </Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Unbalanced
            </Badge>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Left Column - Assets */}
        <div className="space-y-4">
          <BalanceSheetSection 
            title="Assets" 
            items={data.assets.items} 
            total={data.assets.total}
            color="bg-blue-50 dark:bg-blue-900/20"
          />
          
          {/* Total Assets */}
          <Card className="border-2 border-blue-500">
            <CardContent className="py-4 flex items-center justify-between">
              <span className="font-bold">Total Assets</span>
              <span className="text-xl font-bold font-mono text-blue-600">
                {formatCurrency(data.assets.total)}
              </span>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Liabilities & Equity */}
        <div className="space-y-4">
          <BalanceSheetSection 
            title="Liabilities" 
            items={data.liabilities.items} 
            total={data.liabilities.total}
            color="bg-red-50 dark:bg-red-900/20"
          />
          
          <BalanceSheetSection 
            title="Equity" 
            items={data.equity.items} 
            total={data.equity.total}
            color="bg-purple-50 dark:bg-purple-900/20"
          />
          
          {/* Total Liabilities + Equity */}
          <Card className="border-2 border-purple-500">
            <CardContent className="py-4 flex items-center justify-between">
              <span className="font-bold">Total Liabilities + Equity</span>
              <span className="text-xl font-bold font-mono text-purple-600">
                {formatCurrency(data.total_liabilities_and_equity)}
              </span>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Main Reports Component
export default function FinancialReports({ user }) {
  const [activeReport, setActiveReport] = useState('trial-balance');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    'trial-balance': null,
    'income-statement': null,
    'balance-sheet': null
  });
  
  // Date filters
  const [asOfDate, setAsOfDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [startDate, setStartDate] = useState(format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const fetchReport = useCallback(async (reportType) => {
    setLoading(true);
    try {
      let url = `${API}/finance/hub/reports/${reportType}`;
      const params = new URLSearchParams();
      
      if (reportType === 'trial-balance' || reportType === 'balance-sheet') {
        params.append('as_of_date', asOfDate);
      } else if (reportType === 'income-statement') {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      
      const response = await axios.get(`${url}?${params}`);
      setReportData(prev => ({ ...prev, [reportType]: response.data }));
    } catch (err) {
      console.error('Error fetching report:', err);
      toast.error(`Failed to load ${reportType.replace('-', ' ')}`);
    } finally {
      setLoading(false);
    }
  }, [asOfDate, startDate, endDate]);

  useEffect(() => {
    fetchReport(activeReport);
  }, [activeReport, fetchReport]);

  const handleRefresh = () => {
    fetchReport(activeReport);
  };

  return (
    <div className="space-y-6" data-testid="financial-reports">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
            <FileText className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Financial Reports</h2>
            <p className="text-sm text-muted-foreground">Generate and view financial statements</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeReport} onValueChange={setActiveReport}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="trial-balance" className="flex items-center gap-2">
            <Scale className="h-4 w-4" />
            Trial Balance
          </TabsTrigger>
          <TabsTrigger value="income-statement" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Income Statement
          </TabsTrigger>
          <TabsTrigger value="balance-sheet" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Balance Sheet
          </TabsTrigger>
        </TabsList>

        {/* Date Filters */}
        <Card className="mt-4">
          <CardContent className="py-4">
            {(activeReport === 'trial-balance' || activeReport === 'balance-sheet') ? (
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">As of Date</Label>
                  <Input
                    type="date"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                <Button onClick={handleRefresh} size="sm" disabled={loading}>
                  Generate Report
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                <Button onClick={handleRefresh} size="sm" disabled={loading} className="mt-auto">
                  Generate Report
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Content */}
        <TabsContent value="trial-balance" className="mt-4">
          <TrialBalanceReport 
            data={reportData['trial-balance']} 
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="income-statement" className="mt-4">
          <IncomeStatementReport 
            data={reportData['income-statement']} 
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="balance-sheet" className="mt-4">
          <BalanceSheetReport 
            data={reportData['balance-sheet']} 
            loading={loading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
